import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import BrandCategoryLogo from './BrandCategoryLogo'
import { BRAND_CATEGORY_TILES, type BrandCategoryTile } from '../utils/brandCategoryTiles'
import { useInfiniteMarquee } from '../hooks/useInfiniteMarquee'
import type { BrowsePreset } from './MarketplaceHome'

type Props = {
  onOpenCatalog: (preset?: BrowsePreset) => void
}

export default function BrandCategoryRail({ onOpenCatalog }: Props) {
  const { t } = useTranslation()
  const marqueeRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)

  const { dragEndedRef } = useInfiniteMarquee(true, marqueeRef, trackRef, {
    loopSeconds: 88,
    draggingClassName: 'vc-brands-marquee--dragging',
    pauseOnHover: false,
  })

  function openPreset(c: BrandCategoryTile) {
    onOpenCatalog(Object.keys(c.preset).length ? c.preset : undefined)
  }

  function renderTile(c: BrandCategoryTile, setIndex: number) {
    return (
      <button
        key={`${c.key}-${setIndex}`}
        type="button"
        role="listitem"
        className="vc-category-tile"
        onPointerDown={() => {
          dragEndedRef.current = false
        }}
        onClick={() => {
          if (dragEndedRef.current) {
            dragEndedRef.current = false
            return
          }
          openPreset(c)
        }}
      >
        <span className="vc-category-visual vc-category-visual--brand-logo">
          <BrandCategoryLogo brand={c.key} size={48} />
        </span>
        <span className="vc-category-label">{t(`vc.cat.${c.key}`)}</span>
      </button>
    )
  }

  return (
    <div className="vc-brands-scroll-wrap vc-brands-scroll-wrap--marquee">
      <div
        ref={marqueeRef}
        className="vc-brands-marquee"
        aria-label={t('vc.shopByBrand')}
      >
        <div ref={trackRef} className="vc-brands-marquee__track">
          {[0, 1].map((setIndex) => (
            <div
              key={setIndex}
              className={`vc-category-grid vc-category-grid--brands vc-brands-marquee__set${setIndex === 1 ? ' vc-brands-marquee__set--clone' : ''}`}
              role={setIndex === 0 ? 'list' : undefined}
              aria-hidden={setIndex === 1 ? true : undefined}
            >
              {BRAND_CATEGORY_TILES.map((c) => renderTile(c, setIndex))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
